import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FinancialDashboard } from '../../components/ui/financial-dashboard';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, AlertTriangle, CheckCircle, Ship, Users, ChevronDown, ChevronUp, Target, Anchor, ShoppingCart, ArrowDown, ArrowUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRUISE_COLORS = {
  'tour-de-corse-croisiere-catamaran':       { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
  'croisiere-catamaran-corse-du-sud':        { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300' },
  'croisiere-catamaran-ouest-corse':         { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
  'croisiere-catamaran-sardaigne-corse':     { bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-300' },
  'croisiere-catamaran-grece-authentique':   { bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-300' },
  'croisiere-catamaran-grenadines-caraibes': { bg: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-300' },
};

const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const AdminFinancier = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCruise, setFilterCruise] = useState('all');
  const [expandedDeps, setExpandedDeps] = useState({});
  const [contractFinancial, setContractFinancial] = useState(null);
  const [marginData, setMarginData] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/financial/overview`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch { toast.error('Erreur chargement donnees financieres'); }
      finally { setLoading(false); }
    };
    const fetchContractFinancial = async () => {
      try {
        const contracts = await axios.get(`${API}/admin/owner-contracts`, { headers: { Authorization: `Bearer ${token}` } });
        const active = (contracts.data || []).find(c => c.commission_config?.enabled);
        if (active) {
          const fin = await axios.get(`${API}/admin/owner-contracts/${active.id}/financial`, { headers: { Authorization: `Bearer ${token}` } });
          setContractFinancial(fin.data);
        }
      } catch { /* silent */ }
    };
    const fetchMarginData = async () => {
      try {
        const res = await axios.get(`${API}/financial/margin-per-passenger`, { headers: { Authorization: `Bearer ${token}` } });
        setMarginData(res.data);
      } catch { /* silent */ }
    };
    if (token) { fetchData(); fetchContractFinancial(); fetchMarginData(); }
  }, [token]);

  const filteredDeps = useMemo(() => {
    if (!data) return [];
    return data.departures.filter(d => filterCruise === 'all' || d.cruise_slug === filterCruise);
  }, [data, filterCruise]);

  const filteredStats = useMemo(() => {
    if (!filteredDeps.length) return { total_due: 0, total_paid: 0, total_remaining: 0, total_passengers: 0 };
    return {
      total_due: filteredDeps.reduce((s, d) => s + d.total_due, 0),
      total_paid: filteredDeps.reduce((s, d) => s + d.total_paid, 0),
      total_remaining: filteredDeps.reduce((s, d) => s + d.total_remaining, 0),
      total_passengers: filteredDeps.reduce((s, d) => s + Math.max(d.passengers.length, d.passenger_count_from_bookings || 0), 0),
    };
  }, [filteredDeps]);

  const toggleDep = (depId) => setExpandedDeps(prev => ({ ...prev, [depId]: !prev[depId] }));

  const uniqueCruises = useMemo(() => {
    if (!data) return [];
    const map = {};
    data.departures.forEach(d => { map[d.cruise_slug] = d.cruise_name; });
    return Object.entries(map);
  }, [data]);

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  if (loading) return (
    <AdminLayout title="Financier">
      <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-slate-200 rounded-lg animate-pulse" />)}</div>
    </AdminLayout>
  );

  const stats = filterCruise === 'all' && data ? data.stats : filteredStats;

  return (
    <AdminLayout title="Financier">
      <div className="space-y-6" data-testid="admin-financier">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border-0 shadow-md bg-gradient-to-br from-[#0A2540] to-[#1a3a5c]" data-testid="kpi-total-due">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-[10px] sm:text-xs text-white/60 uppercase tracking-wider">Total a facturer</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-white">{fmt(stats.total_due)} <span className="text-sm">EUR</span></div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-600 to-emerald-700" data-testid="kpi-total-paid">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-emerald-200" />
                <span className="text-[10px] sm:text-xs text-white/60 uppercase tracking-wider">Total encaisse</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-white">{fmt(stats.total_paid)} <span className="text-sm">EUR</span></div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-red-500 to-red-600" data-testid="kpi-remaining">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-red-200" />
                <span className="text-[10px] sm:text-xs text-white/60 uppercase tracking-wider">Reste a encaisser</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-white">{fmt(stats.total_remaining)} <span className="text-sm">EUR</span></div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-[#D4AF37] to-[#c49a2e]" data-testid="kpi-passengers">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-white/80" />
                <span className="text-[10px] sm:text-xs text-white/60 uppercase tracking-wider">Passagers</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-white">{stats.total_passengers}</div>
            </CardContent>
          </Card>
        </div>

        {/* Progress bar */}
        {stats.total_due > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-[#4A5568]">
              <span>Taux d'encaissement</span>
              <span className="font-bold">{Math.round((stats.total_paid / stats.total_due) * 100)}%</span>
            </div>
            <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all" style={{ width: `${Math.min(100, (stats.total_paid / stats.total_due) * 100)}%` }} />
            </div>
          </div>
        )}

        {/* Projection & Marges SOGNUDIMARE */}
        {contractFinancial?.enabled && (
          <div className="border-t pt-6">
            <FinancialDashboard data={contractFinancial} viewMode="admin" />
          </div>
        )}

        {/* ===== MARGE NETTE PAR PASSAGER ===== */}
        {marginData && (
          <div className="border-t pt-6 space-y-4">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-[#C4724F]" />
              <h3 className="text-base font-bold text-[#0A2540]">Marge nette par passager</h3>
            </div>

            {/* Global KPI */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Card className="border-0 shadow-md bg-gradient-to-br from-[#0A2540] to-[#1a3a5c]">
                <CardContent className="p-3">
                  <p className="text-[9px] text-white/60 uppercase tracking-wider">CA moyen / pax</p>
                  <p className="text-lg font-bold text-white mt-1">{fmt(marginData.global.revenue_per_pax)} €</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md bg-gradient-to-br from-[#D4AF37] to-[#c49a2e]">
                <CardContent className="p-3">
                  <p className="text-[9px] text-white/60 uppercase tracking-wider">Rétrocession / pax</p>
                  <p className="text-lg font-bold text-white mt-1">{fmt(marginData.global.retrocession_per_pax)} €</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md bg-gradient-to-br from-amber-500 to-amber-600">
                <CardContent className="p-3">
                  <p className="text-[9px] text-white/60 uppercase tracking-wider">Charges / pax</p>
                  <p className="text-lg font-bold text-white mt-1">{fmt(marginData.global.charges_per_pax)} €</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md" style={{ background: `linear-gradient(135deg, ${marginData.global.margin_brute_per_pax >= 0 ? '#059669' : '#DC2626'}, ${marginData.global.margin_brute_per_pax >= 0 ? '#047857' : '#B91C1C'})` }}>
                <CardContent className="p-3">
                  <p className="text-[9px] text-white/60 uppercase tracking-wider">Marge brute / pax</p>
                  <p className="text-lg font-bold text-white mt-1">{fmt(marginData.global.margin_brute_per_pax)} €</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md" style={{ background: `linear-gradient(135deg, ${marginData.global.margin_nette_per_pax >= 0 ? '#059669' : '#DC2626'}, ${marginData.global.margin_nette_per_pax >= 0 ? '#047857' : '#B91C1C'})` }}>
                <CardContent className="p-3">
                  <p className="text-[9px] text-white/60 uppercase tracking-wider">Marge nette / pax</p>
                  <p className="text-lg font-bold text-white mt-1">{fmt(marginData.global.margin_nette_per_pax)} €</p>
                  <p className="text-[9px] text-white/40 mt-0.5">{marginData.global.total_passengers} passagers</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md" style={{ background: `linear-gradient(135deg, ${marginData.global.total_margin_nette >= 0 ? '#059669' : '#DC2626'}, ${marginData.global.total_margin_nette >= 0 ? '#047857' : '#B91C1C'})` }}>
                <CardContent className="p-3">
                  <p className="text-[9px] text-white/60 uppercase tracking-wider">Marge nette totale</p>
                  <p className="text-lg font-bold text-white mt-1">{fmt(marginData.global.total_margin_nette)} €</p>
                </CardContent>
              </Card>
            </div>

            {/* Bar chart per cruise */}
            {marginData.by_cruise.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h4 className="text-sm font-bold text-[#0A2540] mb-3">Marge nette par passager — par croisière</h4>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={marginData.by_cruise} layout="vertical" barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${v}€`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={180} />
                      <Tooltip formatter={(v) => `${fmt(v)} €`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="revenue_per_pax" name="CA / pax" fill="#0A2540" radius={[0,4,4,0]} barSize={14} />
                      <Bar dataKey="retrocession_per_pax" name="Rétrocession / pax" fill="#D4AF37" radius={[0,4,4,0]} barSize={14} />
                      <Bar dataKey="charges_per_pax" name="Charges / pax" fill="#F59E0B" radius={[0,4,4,0]} barSize={14} />
                      <Bar dataKey="margin_nette_per_pax" name="Marge nette / pax" radius={[0,4,4,0]} barSize={14}>
                        {marginData.by_cruise.map((entry, i) => (
                          <Cell key={i} fill={entry.margin_nette_per_pax >= 0 ? '#10B981' : '#EF4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Table detail */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#0A2540] text-white">
                        <th className="text-left p-3 font-medium">Croisière</th>
                        <th className="text-center p-3 font-medium">Pax</th>
                        <th className="text-center p-3 font-medium">Départs</th>
                        <th className="text-right p-3 font-medium">CA / pax</th>
                        <th className="text-right p-3 font-medium">Rétrocession / pax</th>
                        <th className="text-right p-3 font-medium">Charges / pax</th>
                        <th className="text-right p-3 font-medium">Marge brute / pax</th>
                        <th className="text-right p-3 font-medium">Marge nette / pax</th>
                        <th className="text-right p-3 font-medium">Marge nette totale</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {marginData.by_cruise.map((c, i) => (
                        <tr key={c.slug} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-3 font-semibold text-[#0A2540]">{c.name}</td>
                          <td className="p-3 text-center">{c.passengers}</td>
                          <td className="p-3 text-center">{c.departures}</td>
                          <td className="p-3 text-right font-mono font-medium">{fmt(c.revenue_per_pax)} €</td>
                          <td className="p-3 text-right font-mono text-[#D4AF37]">{fmt(c.retrocession_per_pax)} €</td>
                          <td className="p-3 text-right font-mono text-amber-600">{fmt(c.charges_per_pax)} €</td>
                          <td className="p-3 text-right font-mono">{fmt(c.margin_brute_per_pax)} €</td>
                          <td className={`p-3 text-right font-mono font-bold ${c.margin_nette_per_pax >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {fmt(c.margin_nette_per_pax)} €
                          </td>
                          <td className={`p-3 text-right font-mono font-bold ${c.total_margin_nette >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {fmt(c.total_margin_nette)} €
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#0A2540]/5 font-bold text-[#0A2540] border-t-2 border-[#0A2540]">
                        <td className="p-3">GLOBAL</td>
                        <td className="p-3 text-center">{marginData.global.total_passengers}</td>
                        <td className="p-3 text-center">—</td>
                        <td className="p-3 text-right font-mono">{fmt(marginData.global.revenue_per_pax)} €</td>
                        <td className="p-3 text-right font-mono text-[#D4AF37]">{fmt(marginData.global.retrocession_per_pax)} €</td>
                        <td className="p-3 text-right font-mono text-amber-600">{fmt(marginData.global.charges_per_pax)} €</td>
                        <td className="p-3 text-right font-mono">{fmt(marginData.global.margin_brute_per_pax)} €</td>
                        <td className={`p-3 text-right font-mono font-bold ${marginData.global.margin_nette_per_pax >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {fmt(marginData.global.margin_nette_per_pax)} €
                        </td>
                        <td className={`p-3 text-right font-mono font-bold ${marginData.global.total_margin_nette >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {fmt(marginData.global.total_margin_nette)} €
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter by cruise */}
        <div className="flex items-center justify-between">
          <p className="text-[#4A5568] text-sm">{filteredDeps.length} depart{filteredDeps.length > 1 ? 's' : ''} avec activite financiere</p>
          <Select value={filterCruise} onValueChange={setFilterCruise}>
            <SelectTrigger className="w-full sm:w-[250px]" data-testid="financial-filter">
              <SelectValue placeholder="Filtrer par croisiere" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les croisieres</SelectItem>
              {uniqueCruises.map(([slug, name]) => (
                <SelectItem key={slug} value={slug}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Departures list */}
        {filteredDeps.length > 0 ? (
          <div className="space-y-3">
            {filteredDeps.map(dep => {
              const cc = CRUISE_COLORS[dep.cruise_slug] || { bg: 'bg-gray-500', light: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300' };
              const isExpanded = expandedDeps[dep.departure_id];
              const paidPct = dep.total_due > 0 ? Math.round((dep.total_paid / dep.total_due) * 100) : 0;
              return (
                <Card key={dep.departure_id} className={`overflow-hidden transition-all ${isExpanded ? 'ring-1 ring-[#D4AF37]' : ''}`} data-testid={`fin-dep-${dep.departure_id}`}>
                  <button className="w-full text-left" onClick={() => toggleDep(dep.departure_id)}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-2 h-10 rounded-full shrink-0 ${cc.bg}`} />
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-[#0A2540] truncate">{dep.cruise_name}</div>
                            <div className="text-xs text-[#4A5568]">{formatDate(dep.start_date)} - {formatDate(dep.end_date)} | {Math.max(dep.passengers.length, dep.passenger_count_from_bookings || 0)} passager{Math.max(dep.passengers.length, dep.passenger_count_from_bookings || 0) > 1 ? 's' : ''}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-5 pl-5 sm:pl-0">
                          <div className="text-right">
                            <div className="text-xs text-[#94A3B8]">Total</div>
                            <div className="font-bold text-[#0A2540]">{fmt(dep.total_due)} EUR</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-emerald-600">Encaisse</div>
                            <div className="font-bold text-emerald-600">{fmt(dep.total_paid)} EUR</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-red-500">Reste</div>
                            <div className={`font-bold ${dep.total_remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(dep.total_remaining)} EUR</div>
                          </div>
                          <Badge className={`${paidPct >= 100 ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : paidPct > 50 ? 'bg-yellow-100 text-yellow-700 border-yellow-300' : 'bg-red-100 text-red-700 border-red-300'} border`}>
                            {paidPct}%
                          </Badge>
                          {isExpanded ? <ChevronUp className="h-4 w-4 text-[#94A3B8]" /> : <ChevronDown className="h-4 w-4 text-[#94A3B8]" />}
                        </div>
                      </div>
                      {/* Mini progress */}
                      {dep.total_due > 0 && (
                        <div className="mt-2 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(100, paidPct)}%` }} />
                        </div>
                      )}
                    </CardContent>
                  </button>

                  {isExpanded && dep.passengers.length > 0 && (
                    <div className="border-t border-[#E2E8F0] bg-[#F8F9FA] p-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[#94A3B8] uppercase tracking-wider">
                            <th className="text-left pb-2 font-medium">Passager</th>
                            <th className="text-left pb-2 font-medium hidden sm:table-cell">Cab.</th>
                            <th className="text-right pb-2 font-medium">Prix</th>
                            <th className="text-right pb-2 font-medium">CdB</th>
                            <th className="text-right pb-2 font-medium">Total</th>
                            <th className="text-right pb-2 font-medium">Paye</th>
                            <th className="text-right pb-2 font-medium">Reste</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2E8F0]">
                          {dep.passengers.map(p => (
                            <tr key={p.id} className="text-[#4A5568]">
                              <td className="py-1.5 font-medium text-[#0A2540]">{p.name}</td>
                              <td className="py-1.5 hidden sm:table-cell">{p.cabin_number || '-'}</td>
                              <td className="py-1.5 text-right">{fmt(p.price_per_person)}</td>
                              <td className="py-1.5 text-right">{fmt(p.caisse_de_bord)}</td>
                              <td className="py-1.5 text-right font-medium">{fmt(p.total_due)}</td>
                              <td className="py-1.5 text-right text-emerald-600 font-medium">{fmt(p.amount_paid)}</td>
                              <td className={`py-1.5 text-right font-bold ${p.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {p.remaining > 0 ? fmt(p.remaining) : 'OK'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-bold text-[#0A2540] border-t-2 border-[#0A2540]/20">
                            <td className="pt-2" colSpan={2}>TOTAL</td>
                            <td className="pt-2 text-right hidden sm:table-cell"></td>
                            <td className="pt-2 text-right"></td>
                            <td className="pt-2 text-right">{fmt(dep.total_due)}</td>
                            <td className="pt-2 text-right text-emerald-600">{fmt(dep.total_paid)}</td>
                            <td className={`pt-2 text-right ${dep.total_remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(dep.total_remaining)}</td>
                          </tr>
                        </tfoot>
                      </table>
                      {dep.passengers.some(p => p.payment_notes) && (
                        <div className="mt-2 space-y-1">
                          {dep.passengers.filter(p => p.payment_notes).map(p => (
                            <div key={p.id} className="text-[10px] text-[#94A3B8] italic">{p.name}: {p.payment_notes}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <DollarSign className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#4A5568]">Aucune donnee financiere pour le moment</p>
              <p className="text-xs text-[#94A3B8] mt-1">Ajoutez des passagers avec des montants depuis le Planning</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFinancier;
